/**
 * Modulo M3: aula in diretta. Player WebRTC (WHEP) con ripiego LL-HLS, slide sincronizzate,
 * domande pre-moderate, assistenza, flusso dalla regia o dal computer del relatore,
 * eventi WebSocket dell'aula. Il browser dei partecipanti non pubblica mai audio o video.
 */
import { z } from 'zod';
import { MODALITA_INGRESSO, MODALITA_PARTECIPAZIONE, STATI_WEBINAR, paginazioneSchema } from './percorsi.js';

const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });

export const STATI_DOMANDA = ['in_attesa', 'inoltrata', 'risposta', 'scartata'] as const;
export type StatoDomanda = (typeof STATI_DOMANDA)[number];
export const STATI_ASSISTENZA = ['aperta', 'risposta', 'chiusa'] as const;
export type StatoAssistenza = (typeof STATI_ASSISTENZA)[number];
export const TRASPORTI_VIDEO = ['webrtc', 'hls'] as const;
export type TrasportoVideo = (typeof TRASPORTI_VIDEO)[number];
export const NODI_VIDEO = ['video1', 'video2'] as const;
export type NodoVideo = (typeof NODI_VIDEO)[number];

export const LIMITI_AULA = {
  /** minuti prima dell'inizio previsto in cui si apre la sala d'attesa */
  apertaDaMinuti: 60,
  domandaMaxCaratteri: 500,
  domandeMaxPerWebinar: 10,
  domandaOgniSecondi: 60,
  assistenzaMaxCaratteri: 1000,
  slideMaxMb: 50,
  /** secondi di tentativi WebRTC prima del ripiego HLS */
  ripiegoHlsDopoSecondi: 8,
} as const;

// ---------------------------------------------------------------- Accesso all'aula
export const urlVideoSchema = z.object({
  nodo: z.enum(NODI_VIDEO),
  /**
   * URL di segnalazione WebSocket di OvenMediaEngine (ws/wss), già firmato e con ?transport=all
   * (UDP, poi TCP diretto, poi TURN integrato: l'offerta porta `ice_servers`/`iceServers` da passare a RTCPeerConnection).
   * OME non implementa WHEP in lettura: il player apre il WebSocket, manda {"command":"request_offer"},
   * riceve {"command":"offer","id","peer_id","sdp":{type,sdp},"candidates":[...],"ice_servers":[...]},
   * risponde {"id","peer_id","command":"answer","sdp":{type:"answer",sdp}} e manda i propri candidati
   * con {"id","peer_id","command":"candidate","candidates":[...]}; alla fine {"command":"stop"}.
   * `ice_servers` (TURN integrato) va passato a RTCPeerConnection; `?transport=tcp` forza il relay.
   */
  webrtc: z.string().url(),
  /** playlist LL-HLS di ripiego (hls.js), già firmata */
  hls: z.string().url(),
  /** scadenza degli URL firmati */
  scadeAt: dataIso,
});
export type UrlVideo = z.infer<typeof urlVideoSchema>;

export const slideInfoSchema = z.object({
  /** URL firmato del PDF (S3), null se non caricato */
  url: z.string().url().nullable(),
  nomeFile: z.string().nullable(),
  pagine: z.number().int().nullable(),
  paginaCorrente: z.number().int().min(1),
});
export type SlideInfo = z.infer<typeof slideInfoSchema>;

export const accessoAulaSchema = z.object({
  webinar: z.object({
    id,
    titolo: z.string(),
    stato: z.enum(STATI_WEBINAR),
    inizioPrevisto: dataIso,
    finePrevista: dataIso,
    inizioEffettivo: dataIso.nullable(),
    fineEffettiva: dataIso.nullable(),
    durataUfficialeMin: z.number().int(),
    sogliaPresenzaPct: z.number().int(),
    relatori: z.array(z.object({ nome: z.string(), cognome: z.string() })),
  }),
  modalita: z.enum(MODALITA_PARTECIPAZIONE),
  /** null se la diretta non è ancora iniziata o il partecipante è in sala */
  video: urlVideoSchema.nullable(),
  slide: slideInfoSchema,
  /** ora del server, per allineare countdown e timer */
  oraServer: dataIso,
  /** apertura della sala d'attesa */
  apertaDa: dataIso,
});
export type AccessoAula = z.infer<typeof accessoAulaSchema>;

// ---------------------------------------------------------------- Slide
export const paginaSlideSchema = z.object({ pagina: z.number().int().min(1).max(2000) });
export const slideCaricataSchema = slideInfoSchema;

// ---------------------------------------------------------------- Domande del pubblico
export const nuovaDomandaSchema = z.object({
  testo: z.string().trim().min(5, 'Scrivi almeno 5 caratteri.').max(LIMITI_AULA.domandaMaxCaratteri, `La domanda può avere al massimo ${LIMITI_AULA.domandaMaxCaratteri} caratteri.`),
});
export const domandaMiaSchema = z.object({
  id,
  testo: z.string(),
  stato: z.enum(STATI_DOMANDA),
  inviataAt: dataIso,
  rispostaAt: dataIso.nullable(),
});
export type DomandaMia = z.infer<typeof domandaMiaSchema>;

/** Coda di moderazione: solo l'admin vede l'autore. */
export const domandaAdminSchema = z.object({
  id,
  testo: z.string(),
  testoOriginale: z.string().nullable(),
  stato: z.enum(STATI_DOMANDA),
  autore: z.object({ userId: id, nome: z.string(), cognome: z.string(), modalita: z.enum(MODALITA_PARTECIPAZIONE).nullable() }),
  inviataAt: dataIso,
  moderataAt: dataIso.nullable(),
  ordineInoltro: z.number().int().nullable(),
  rispostaAt: dataIso.nullable(),
});
export type DomandaAdmin = z.infer<typeof domandaAdminSchema>;
export const ricercaDomandeSchema = paginazioneSchema.extend({ stato: z.enum(STATI_DOMANDA).optional() });
export const modificaDomandaSchema = nuovaDomandaSchema;
export const scartaDomandaSchema = z.object({ motivo: z.string().trim().max(200).optional().or(z.literal('')) });

/** Vista del relatore: MAI l'autore. */
export const domandaRelatoreSchema = z.object({
  id,
  testo: z.string(),
  stato: z.enum(['inoltrata', 'risposta']),
  ordineInoltro: z.number().int(),
  inoltrataAt: dataIso,
});
export type DomandaRelatore = z.infer<typeof domandaRelatoreSchema>;

// ---------------------------------------------------------------- Assistenza ("Serve aiuto?")
export const nuovaAssistenzaSchema = z.object({
  testo: z.string().trim().min(5, 'Descrivi il problema in almeno 5 caratteri.').max(LIMITI_AULA.assistenzaMaxCaratteri),
});
export const assistenzaMiaSchema = z.object({ id, testo: z.string(), risposta: z.string().nullable(), stato: z.enum(STATI_ASSISTENZA), inviataAt: dataIso, rispostaAt: dataIso.nullable() });
export type AssistenzaMia = z.infer<typeof assistenzaMiaSchema>;
export const assistenzaAdminSchema = assistenzaMiaSchema.extend({ autore: z.object({ userId: id, nome: z.string(), cognome: z.string(), email: z.string() }) });
export type AssistenzaAdmin = z.infer<typeof assistenzaAdminSchema>;
export const ricercaAssistenzaSchema = paginazioneSchema.extend({ stato: z.enum(STATI_ASSISTENZA).optional() });
export const rispostaAssistenzaSchema = z.object({ risposta: z.string().trim().min(1).max(1000), chiudi: z.boolean().default(true) });

// ---------------------------------------------------------------- Flusso e nodi video
export const statoFlussoSchema = z.object({
  nodoAttivo: z.enum(NODI_VIDEO),
  ricevuto: z.boolean(),
  origine: z.enum(['srt', 'rtmp', 'whip', 'sconosciuta']).nullable(),
  video: z.object({ codec: z.string(), larghezza: z.number().int(), altezza: z.number().int(), fps: z.number(), bitrateKbps: z.number() }).nullable(),
  audio: z.object({ codec: z.string(), bitrateKbps: z.number() }).nullable(),
  qualitaGenerate: z.array(z.string()),
  spettatori: z.object({ webrtc: z.number().int(), hls: z.number().int() }),
  /** stato di ogni nodo configurato */
  nodi: z.array(z.object({ nome: z.enum(NODI_VIDEO), raggiungibile: z.boolean(), ricevuto: z.boolean() })),
  rilevatoAt: dataIso,
});
export type StatoFlusso = z.infer<typeof statoFlussoSchema>;

/** Indirizzi per la regia: uno per nodo. */
export const indirizziRegiaSchema = z.object({
  modalitaIngresso: z.enum(MODALITA_INGRESSO),
  streamKey: z.string(),
  nodi: z.array(z.object({ nome: z.enum(NODI_VIDEO), srt: z.string(), rtmpUrl: z.string(), rtmpKey: z.string() })),
  nodoAttivo: z.enum(NODI_VIDEO),
});
export type IndirizziRegia = z.infer<typeof indirizziRegiaSchema>;

export const cambioNodoSchema = z.object({ nodo: z.enum(NODI_VIDEO) });

/** Trasmissione dal computer del relatore: endpoint WHIP (POST application/sdp → 201 + SDP answer, Location per PATCH/DELETE). */
export const trasmissioneRelatoreSchema = z.object({ whip: z.string().url(), scadeAt: dataIso, nodo: z.enum(NODI_VIDEO) });
export type TrasmissioneRelatore = z.infer<typeof trasmissioneRelatoreSchema>;

/** Anteprima (admin, regia, relatore): URL WHEP firmato senza vincolo di conferma. */
export const anteprimaSchema = urlVideoSchema;

// ---------------------------------------------------------------- Eventi WebSocket /ws/aula/:webinarId
export const eventoAulaSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('benvenuto'), oraServer: dataIso, stato: z.enum(STATI_WEBINAR), slidePagina: z.number().int(), nodoAttivo: z.enum(NODI_VIDEO) }),
  z.object({ tipo: z.literal('stato'), stato: z.enum(STATI_WEBINAR), inizioEffettivo: dataIso.nullable(), fineEffettiva: dataIso.nullable() }),
  z.object({ tipo: z.literal('slide'), pagina: z.number().int() }),
  z.object({ tipo: z.literal('nodo'), nodo: z.enum(NODI_VIDEO) }),
  /** solo all'autore */
  z.object({ tipo: z.literal('domanda'), domanda: domandaMiaSchema }),
  /** solo a admin e relatore (senza autore per il relatore) */
  z.object({ tipo: z.literal('coda_domande'), inAttesa: z.number().int(), inoltrate: z.number().int() }),
  /** solo ad admin: richieste di assistenza aperte */
  z.object({ tipo: z.literal('coda_assistenza'), aperte: z.number().int() }),
  z.object({ tipo: z.literal('domanda_relatore'), domanda: domandaRelatoreSchema }),
  z.object({ tipo: z.literal('assistenza'), richiesta: assistenzaMiaSchema }),
  z.object({ tipo: z.literal('ping'), oraServer: dataIso }),
]);
export type EventoAula = z.infer<typeof eventoAulaSchema>;

/** Messaggi dal client al server (pochi: tutto il resto passa da REST). */
export const messaggioClientAulaSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('pong') }),
  z.object({ tipo: z.literal('trasporto'), trasporto: z.enum(TRASPORTI_VIDEO) }),
]);
export type MessaggioClientAula = z.infer<typeof messaggioClientAulaSchema>;
